import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { Lock, Unlock, Upload, Loader2, Download, Monitor, ShoppingCart, Zap, AlertTriangle, FileType, History, CheckCircle, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const parseMMFFilename = (filename) => {
  if (!filename) return null;
  const name = filename.toLowerCase();
  if (name.includes('int_flash') || name.includes('intflash') || name.includes('internal_flash'))
    return { type: 'int_flash', label: 'Internal Flash' };
  if (name.includes('ext_flash') || name.includes('extflash') || name.includes('external_flash'))
    return { type: 'ext_flash', label: 'External Flash' };
  if (name.includes('int_eeprom') || name.includes('internal_eeprom'))
    return { type: 'int_eeprom', label: 'Internal EEPROM' };
  if (name.includes('ext_eeprom') || name.includes('external_eeprom'))
    return { type: 'ext_eeprom', label: 'External EEPROM' };
  if (name.includes('eeprom'))
    return { type: 'eeprom', label: 'EEPROM' };
  if (name.includes('flash'))
    return { type: 'flash', label: 'Flash' };
  if (name.includes('micro') || name.includes('uC'))
    return { type: 'micro', label: 'Microcontroller' };
  return null;
};

const formatDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' }) + ' ' +
    d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
};

const formatSize = (bytes) => {
  if (!bytes) return '-';
  if (bytes < 1024) return bytes + ' B';
  return (bytes / 1024).toFixed(0) + ' KB';
};

const MMSSessionPanel = ({ headers }) => {
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState('');
  const [needs2fa, setNeeds2fa] = useState(false);

  const fetchHealth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mms/health`, { headers });
      setHealth(res.data);
      setNeeds2fa(res.data.status === 'awaiting_2fa');
    } catch { /* ignore */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  const handleRelogin = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/mms/relogin`, {}, { headers });
      if (res.data.success) {
        toast.success('Sesión MMS renovada');
        setNeeds2fa(false);
      } else if (res.data.needs_2fa) {
        toast.info('MMS ha enviado un código 2FA al email. Introdúcelo abajo.');
        setNeeds2fa(true);
      } else {
        toast.error(res.data.error || 'No se pudo renovar la sesión');
      }
      fetchHealth();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error renovando sesión'); }
    finally { setBusy(false); }
  };

  const handleSubmitCode = async () => {
    if (!code.trim()) return toast.error('Introduce el código 2FA');
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('code', code.trim());
      await axios.post(`${API}/mms/2fa-code`, fd, { headers });
      toast.success('Sesión MMS restaurada');
      setCode('');
      setNeeds2fa(false);
      fetchHealth();
    } catch (e) { toast.error(e.response?.data?.detail || 'Código no aceptado'); }
    finally { setBusy(false); }
  };

  const ok = health?.logged_in;
  return (
    <div className="bg-surface border border-white/10 rounded-md p-4 mb-6" data-testid="mms-session-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          {ok ? <CheckCircle className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
          <span className="text-sm text-zinc-300 font-medium">Sesión MMS (MagicMotorsport)</span>
          <span className={`text-xs ${ok ? 'text-green-500' : 'text-red-400'}`} data-testid="mms-session-status">
            {health ? health.message : 'Comprobando...'}
          </span>
        </div>
        <Button size="sm" variant="outline" onClick={handleRelogin} disabled={busy}
          className="border-white/10 text-zinc-300" data-testid="mms-relogin-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Renovar sesión'}
        </Button>
      </div>
      {needs2fa && (
        <div className="mt-3 flex items-center gap-2" data-testid="mms-2fa-form">
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Código 2FA (email)"
            className="max-w-[200px] bg-black/30 border-white/10 text-white" data-testid="mms-2fa-input" />
          <Button size="sm" onClick={handleSubmitCode} disabled={busy} data-testid="mms-2fa-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verificar'}
          </Button>
        </div>
      )}
    </div>
  );
};

const SlaveManagerPage = () => {
  const { user, token, refreshUser } = useAuth();
  const [tools, setTools] = useState([]);
  const [mmsOps, setMmsOps] = useState(0);
  const [mmsFree, setMmsFree] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedSN, setSelectedSN] = useState('');
  const [operation, setOperation] = useState('decrypt');
  const [vehicleId, setVehicleId] = useState('');
  const [file, setFile] = useState(null);
  const [fileTypeInfo, setFileTypeInfo] = useState(null);
  const [flashTypes, setFlashTypes] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [buying, setBuying] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [redownloading, setRedownloading] = useState(null);
  const [liveSlaves, setLiveSlaves] = useState([]);

  const handleRedownload = async (op) => {
    setRedownloading(op.id);
    try {
      const res = await axios.get(`${API}/mms/history/${op.id}/download`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = op.result_filename || 'resultado.bin';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('No se pudo descargar el archivo');
    } finally {
      setRedownloading(null);
    }
  };

  const headers = { Authorization: `Bearer ${token}` };

  const fetchTools = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mms/user-tools`, { headers });
      setTools(res.data.tools || []);
      setMmsOps(res.data.mms_operations || 0);
      setMmsFree(res.data.mms_free || false);
      setIsAdmin(res.data.is_admin || false);
      if (res.data.tools?.length > 0 && !selectedSN) {
        setSelectedSN(res.data.tools[0].machine_sn);
      }
      if (res.data.is_admin) {
        axios.get(`${API}/mms/live-slaves`, { headers }).then(live => {
          const slaves = live.data.slaves || [];
          setLiveSlaves(slaves);
          if (slaves.length > 0) setSelectedSN(prev => prev || slaves[0].sn);
        }).catch(() => { /* live list unavailable, keep assigned tools */ });
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [token]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mms/history`, { headers });
      setHistory(res.data.operations || []);
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => { fetchTools(); fetchHistory(); }, [fetchTools, fetchHistory]);

  const isValidForOperation = (fname, op) => {
    const n = fname.toLowerCase();
    const isMmf = n.endsWith('.mmf') || n.endsWith('.mmf2');
    return op === 'decrypt' ? isMmf : !isMmf;
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f) {
      if (!isValidForOperation(f.name, operation)) {
        toast.error(operation === 'decrypt'
          ? 'El desencriptador solo acepta archivos .mmf o .mmf2'
          : 'El encriptador no acepta archivos .mmf/.mmf2 (ya están encriptados)');
        e.target.value = '';
        return;
      }
      setFile(f);
      const info = parseMMFFilename(f.name);
      setFileTypeInfo(info);
      // Auto-select the detected memory type so MMS receives the correct one.
      const validMem = ['int_flash', 'ext_flash', 'int_eeprom', 'ext_eeprom'];
      let mem = info?.type;
      if (mem === 'flash') mem = 'int_flash';
      else if (mem === 'eeprom') mem = 'int_eeprom';
      setFlashTypes(validMem.includes(mem) ? [mem] : ['int_flash']);
    }
  };

  const handleOperationChange = (op) => {
    setOperation(op);
    if (file && !isValidForOperation(file.name, op)) {
      setFile(null);
      setFileTypeInfo(null);
    }
  };

  const toggleFlashType = (t) => {
    // MMS accepts exactly one memory type per operation → single select.
    setFlashTypes(prev => (prev.length === 1 && prev[0] === t) ? prev : [t]);
  };

  const handleBuyPack = async () => {
    setBuying(true);
    try {
      const res = await axios.post(`${API}/mms/buy-pack`, {}, { headers });
      setMmsOps(res.data.mms_operations);
      toast.success(res.data.message);
      refreshUser();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al comprar pack'); }
    finally { setBuying(false); }
  };

  const handleProcess = async () => {
    if (!selectedSN) return toast.error('Selecciona una herramienta SLAVE');
    if (!file) return toast.error(operation === 'decrypt' ? 'Selecciona un archivo .mmf o .mmf2' : 'Selecciona un archivo');
    if (!isValidForOperation(file.name, operation)) {
      return toast.error(operation === 'decrypt'
        ? 'El desencriptador solo acepta archivos .mmf o .mmf2'
        : 'El encriptador no acepta archivos .mmf/.mmf2');
    }
    if (!vehicleId.trim()) return toast.error('Introduce la matrícula / ID del vehículo');

    setProcessing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('machine_sn', selectedSN);
      fd.append('operation', operation);
      fd.append('vehicle_id', vehicleId.trim());
      fd.append('flash_types', flashTypes.join(','));

      const res = await axios.post(`${API}/mms/process`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        responseType: 'blob',
        timeout: 300000,
      });

      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?(.+?)"?$/);
      const fname = match ? match[1] : `${operation}ed_${file.name}`;

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Archivo ${operation === 'decrypt' ? 'desencriptado' : 'encriptado'} correctamente`);
      if (!isAdmin && !mmsFree) setMmsOps(prev => Math.max(0, prev - 1));
      fetchHistory();
    } catch (err) {
      let msg = 'Error al procesar el archivo';
      try {
        if (err.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          msg = json.detail || msg;
        } else if (err.response?.data?.detail) {
          msg = err.response.data.detail;
        }
      } catch { /* use default msg */ }
      toast.error(msg);
      fetchHistory();
    } finally {
      setProcessing(false);
    }
  };

  const canProcess = isAdmin || mmsFree || mmsOps > 0;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-white" data-testid="slave-manager-title">GESTOR SLAVE</h1>
              <p className="text-zinc-500 text-sm">Encriptar y desencriptar archivos FLEX</p>
            </div>
          </div>
          {history.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setShowHistory(!showHistory)}
              className="text-zinc-400 hover:text-white" data-testid="toggle-history-btn">
              <History className="w-4 h-4 mr-2" />
              Historial ({history.length})
            </Button>
          )}
        </div>

        {/* Admin: MMS session panel */}
        {isAdmin && <MMSSessionPanel headers={headers} />}

        {/* Warning banner */}
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-md p-4 mb-6 flex items-start gap-3" data-testid="slave-warning-banner">
          <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-amber-400 text-sm font-medium">Desencriptar: solo .mmf / .mmf2 · Encriptar: cualquier archivo excepto .mmf/.mmf2</p>
            <p className="text-amber-500/70 text-xs mt-1">
              Únicamente puedes subir archivos asociados a la herramienta SLAVE que tienes asignada.
              Subir archivos de otra máquina provocará un error en MMS.
            </p>
          </div>
        </div>

        {/* History panel */}
        {showHistory && history.length > 0 && (
          <div className="bg-surface border border-white/10 rounded-md p-5 mb-6" data-testid="history-panel">
            <p className="text-zinc-400 text-sm mb-3 font-medium flex items-center gap-2">
              <History className="w-4 h-4" /> HISTORIAL DE OPERACIONES
            </p>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {history.map(op => (
                <div key={op.id} className="flex items-center gap-3 p-3 rounded-md bg-surface-highlight border border-white/5" data-testid={`history-item-${op.id}`}>
                  {op.status === 'success' ? (
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs font-heading font-bold px-1.5 py-0.5 rounded ${op.operation === 'decrypt' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'}`}>
                        {op.operation === 'decrypt' ? 'DEC' : 'ENC'}
                      </span>
                      <span className="text-white text-sm truncate">{op.original_filename}</span>
                      {(op.flash_types || '').split(',').filter(Boolean).map(ft => (
                        <span key={ft} className="text-[9px] font-mono uppercase bg-primary/10 text-primary border border-primary/20 px-1.5 py-0.5 rounded leading-none" data-testid={`history-flash-${op.id}-${ft}`}>
                          {ft.replace('_', ' ')}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-zinc-600 text-xs">
                      <span>{formatDate(op.created_at)}</span>
                      <span>{op.vehicle_id}</span>
                      <span className="font-mono">{op.machine_sn}</span>
                      {op.file_size > 0 && <span>{formatSize(op.file_size)}</span>}
                    </div>
                  </div>
                  {op.status === 'success' && op.result_file_id && (
                    <button onClick={() => handleRedownload(op)} disabled={redownloading === op.id}
                      className="flex items-center gap-1 px-2 py-1.5 rounded-md text-primary hover:bg-primary/10 transition-colors text-xs flex-shrink-0"
                      title={op.result_filename || ''} data-testid={`history-download-${op.id}`}>
                      {redownloading === op.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : (tools.length === 0 && liveSlaves.length === 0) ? (
          <div className="bg-surface border border-white/10 rounded-md p-8 text-center" data-testid="no-tools-message">
            <Monitor className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-400">No tienes herramientas SLAVE asignadas.</p>
            <p className="text-zinc-600 text-sm mt-1">Contacta al administrador para que te asigne una.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Operations balance */}
            {!isAdmin && !mmsFree && (
              <div className="bg-surface border border-white/10 rounded-md p-5" data-testid="operations-balance">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-zinc-400 text-xs uppercase tracking-wider mb-1">Operaciones disponibles</p>
                    <p className="text-3xl font-heading font-bold text-white" data-testid="ops-count">{mmsOps}</p>
                  </div>
                  <Button onClick={handleBuyPack} disabled={buying} className="bg-primary hover:bg-primary/90" data-testid="buy-pack-btn">
                    {buying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShoppingCart className="w-4 h-4 mr-2" />}
                    Comprar Pack (5 ops = 20 créditos)
                  </Button>
                </div>
                {mmsOps === 0 && (
                  <p className="text-red-400 text-xs mt-2">Necesitas comprar un pack para usar el servicio.</p>
                )}
              </div>
            )}

            {/* Step 1: Select tool */}
            <div className="bg-surface border border-white/10 rounded-md p-5" data-testid="step-select-tool">
              <p className="text-zinc-400 text-sm mb-3 font-medium">1. HERRAMIENTA SLAVE</p>
              {isAdmin && liveSlaves.length > 0 ? (
                <div>
                  <select value={selectedSN} onChange={(e) => setSelectedSN(e.target.value)}
                    className="w-full bg-surface-highlight border border-white/10 rounded-md p-3 text-white text-sm focus:border-primary outline-none"
                    data-testid="live-slave-select">
                    {liveSlaves.map(s => (
                      <option key={s.sn} value={s.sn}>
                        {(s.info || 'Slave')}{s.available === false ? ' (no disponible)' : ''} — {s.sn}
                      </option>
                    ))}
                  </select>
                  <p className="text-zinc-600 text-xs mt-2" data-testid="live-slaves-count">
                    {liveSlaves.length} slaves vinculados en vivo desde MagicMotorsport
                  </p>
                </div>
              ) : (
              <div className="grid gap-2">
                {tools.map(t => (
                  <label key={t.id} className={`flex items-center gap-3 p-3 rounded-md cursor-pointer border transition-colors ${selectedSN === t.machine_sn ? 'border-primary bg-primary/5' : 'border-white/5 bg-surface-highlight hover:border-white/10'}`}
                    data-testid={`tool-option-${t.machine_sn}`}>
                    <input type="radio" name="slave_sn" value={t.machine_sn} checked={selectedSN === t.machine_sn} onChange={() => setSelectedSN(t.machine_sn)} className="accent-primary" />
                    <Monitor className="w-4 h-4 text-zinc-500" />
                    <div className="flex-1">
                      <p className="text-white text-sm font-medium">{t.label || t.machine_sn}</p>
                      <p className="text-zinc-600 text-xs font-mono">{t.machine_sn}</p>
                    </div>
                  </label>
                ))}
              </div>
              )}
            </div>

            {/* Step 2: Operation */}
            <div className="bg-surface border border-white/10 rounded-md p-5" data-testid="step-operation">
              <p className="text-zinc-400 text-sm mb-3 font-medium">2. OPERACIÓN</p>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => handleOperationChange('decrypt')} data-testid="btn-decrypt"
                  className={`flex items-center justify-center gap-2 p-4 rounded-md border transition-colors ${operation === 'decrypt' ? 'border-primary bg-primary/10 text-primary' : 'border-white/5 bg-surface-highlight text-zinc-400 hover:border-white/10'}`}>
                  <Unlock className="w-5 h-5" />
                  <span className="font-heading text-sm font-bold">DECRYPT</span>
                </button>
                <button onClick={() => handleOperationChange('encrypt')} data-testid="btn-encrypt"
                  className={`flex items-center justify-center gap-2 p-4 rounded-md border transition-colors ${operation === 'encrypt' ? 'border-primary bg-primary/10 text-primary' : 'border-white/5 bg-surface-highlight text-zinc-400 hover:border-white/10'}`}>
                  <Lock className="w-5 h-5" />
                  <span className="font-heading text-sm font-bold">ENCRYPT</span>
                </button>
              </div>
            </div>

            {/* Step 3: Vehicle ID */}
            <div className="bg-surface border border-white/10 rounded-md p-5" data-testid="step-vehicle-id">
              <p className="text-zinc-400 text-sm mb-3 font-medium">3. MATRÍCULA / ID VEHÍCULO</p>
              <Input value={vehicleId} onChange={e => setVehicleId(e.target.value)} placeholder="Ej: 1234 ABC"
                className="bg-surface-highlight border-white/10 text-white" data-testid="input-vehicle-id" />
            </div>

            {/* Step 4: File */}
            <div className="bg-surface border border-white/10 rounded-md p-5" data-testid="step-file-upload">
              <p className="text-zinc-400 text-sm mb-3 font-medium">
                {operation === 'decrypt' ? '4. ARCHIVO .MMF' : '4. ARCHIVO A ENCRIPTAR'}
              </p>
              <label className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-white/10 rounded-md cursor-pointer hover:border-primary/50 transition-colors"
                data-testid="file-upload-area">
                <Upload className="w-8 h-8 text-zinc-500" />
                <span className="text-zinc-400 text-sm">{file ? file.name : (operation === 'decrypt' ? 'Seleccionar archivo .mmf o .mmf2' : 'Seleccionar archivo (cualquiera excepto .mmf/.mmf2)')}</span>
                {file && <span className="text-zinc-600 text-xs">{(file.size / 1024).toFixed(0)} KB</span>}
                <input type="file" accept={operation === 'decrypt' ? '.mmf,.mmf2' : undefined} onChange={handleFileChange} className="hidden" data-testid="file-input" />
              </label>
              {fileTypeInfo && (
                <div className="mt-3 flex items-center gap-2 bg-primary/5 border border-primary/20 rounded-md px-3 py-2" data-testid="file-type-info">
                  <FileType className="w-4 h-4 text-primary" />
                  <span className="text-primary text-xs font-medium">Tipo detectado: {fileTypeInfo.label}</span>
                  <span className="text-zinc-500 text-xs">({fileTypeInfo.type})</span>
                </div>
              )}
            </div>

            {/* Step 5: Flash type tags for output filename */}
            <div className="bg-surface border border-white/10 rounded-md p-5" data-testid="step-flash-types">
              <p className="text-zinc-400 text-sm mb-1 font-medium">5. TIPO DE MEMORIA</p>
              <p className="text-zinc-600 text-xs mb-3">Debe coincidir con el tipo de memoria del archivo. Se detecta automáticamente, pero puedes cambiarlo.</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'int_flash', label: 'Internal Flash' },
                  { id: 'ext_flash', label: 'External Flash' },
                  { id: 'int_eeprom', label: 'Internal EEPROM' },
                  { id: 'ext_eeprom', label: 'External EEPROM' },
                ].map(ft => (
                  <label key={ft.id} className={`flex items-center gap-2 p-3 rounded-md cursor-pointer border transition-colors ${flashTypes.includes(ft.id) ? 'border-primary bg-primary/5' : 'border-white/5 bg-surface-highlight hover:border-white/10'}`}
                    data-testid={`flash-type-${ft.id}`}>
                    <input type="checkbox" checked={flashTypes.includes(ft.id)} onChange={() => toggleFlashType(ft.id)} className="accent-primary" />
                    <span className="text-white text-sm">{ft.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Submit */}
            <Button onClick={handleProcess} disabled={processing || !file || !selectedSN || !vehicleId.trim() || !canProcess}
              className="w-full bg-primary hover:bg-primary/90 text-white font-heading uppercase tracking-wider h-14 text-base"
              data-testid="submit-process-btn">
              {processing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Procesando en MMS... (puede tardar ~15s)
                </>
              ) : !canProcess ? (
                'COMPRA UN PACK PARA CONTINUAR'
              ) : (
                <>
                  <Download className="w-5 h-5 mr-2" />
                  {operation === 'decrypt' ? 'DESENCRIPTAR' : 'ENCRIPTAR'}
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SlaveManagerPage;
